import { Fragment, type JSX, type ReactNode, useEffect, useState } from 'react';

import {
  type AdminActionResult,
  type AdminAnalytics,
  type AdminBroadcastInput,
  type AdminDeviceDetail,
  type DeviceHealth,
  type AdminFleet,
  type AdminOverview,
  type AdminCustomerDetail,
  type AdminTenantDetail,
  type SystemStatus,
  type SystemStatusLevel,
  adminBackfillBilling,
  adminCreateBroadcast,
  adminCreateCustomer,
  adminCreateTenant,
  adminDeleteBroadcast,
  adminDeleteCustomer,
  adminDeleteDevice,
  adminDeleteTenant,
  adminIssueLicense,
  adminRenameDevice,
  adminReseedDefaults,
  adminResetCustomer2fa,
  adminRevokeLicense,
  adminSetCustomerPassword,
  adminSetDeviceTag,
  adminUnlockCustomer,
  adminUpdateCustomerEmail,
  adminUpdateTenant,
  adminVerifyCustomerEmail,
  fetchAdminAnalytics,
  fetchAdminCustomer,
  fetchAdminDevice,
  fetchAdminFleet,
  fetchAdminOverview,
  fetchAdminWhoami,
  fetchStatus,
  fetchTenantDetail,
} from '../api.js';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Link,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GridViewIcon from '@mui/icons-material/GridView';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import BarChartIcon from '@mui/icons-material/BarChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';

import { getAdminSessionToken, setAdminSessionToken } from '../builder/api.js';
import { downloadCsv } from '../csv.js';
import { confirmDialog, promptDialog } from './dialogs.js';

type DetailState =
  | { id: string; s: 'loading' }
  | { id: string; s: 'ok'; detail: AdminTenantDetail }
  | { id: string; s: 'err'; message: string };

/**
 * Cross-tenant operator console at `?admin=1` — a full-viewport admin layout:
 * a fixed dark sidebar (brand + grouped nav + connection footer) and a main
 * column with a top bar and the active section's content as card panels.
 *
 * Gated by the ADMIN_BEARER_TOKEN (stored in this browser only); not linked
 * from the customer UI.
 */
type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; overview: AdminOverview }
  | { kind: 'err'; message: string };

type Section = 'overview' | 'tenants' | 'customers' | 'fleet' | 'analytics';

const DRAWER_WIDTH = 272;

const NAV: { group: string; items: { id: Section; label: string; icon: ReactNode }[] }[] = [
  {
    group: 'Fleet',
    items: [
      { id: 'overview', label: 'Overview', icon: <GridViewIcon /> },
      { id: 'tenants', label: 'Tenants', icon: <StorageIcon /> },
      { id: 'customers', label: 'Customers', icon: <PeopleIcon /> },
      { id: 'fleet', label: 'Devices', icon: <PhoneAndroidIcon /> },
    ],
  },
  {
    group: 'Insights',
    items: [{ id: 'analytics', label: 'Analytics', icon: <BarChartIcon /> }],
  },
];

type AnalyticsState =
  | { s: 'loading' }
  | { s: 'ok'; data: AdminAnalytics }
  | { s: 'err'; message: string };

type FleetState = { s: 'loading' } | { s: 'ok'; data: AdminFleet } | { s: 'err'; message: string };

/* ─────────────────────────── routing ───────────────────────────
 * The console is a single page, but every location — each section and each
 * drill-down — lives at its own URL hash (`#customers`, `#customers/<id>`,
 * `#tenants/<id>`, `#fleet/<id>`, …). Navigation is plain `<a href>` links, so
 * the browser's history stack records real in-app locations: the Back / Forward
 * buttons (and the mouse's back/forward side buttons, which the browser maps to
 * them) step through the console, not out of it. The `?admin=1` query that
 * mounts this component is untouched — only the fragment changes.
 */
const SECTIONS: readonly Section[] = ['overview', 'tenants', 'customers', 'fleet', 'analytics'];
/** Sections that support a `/:id` drill-down. */
const DETAIL_SECTIONS: ReadonlySet<Section> = new Set<Section>(['tenants', 'customers', 'fleet']);

interface Route {
  section: Section;
  /** The drill-down id when on a `/:id` route, else null. */
  id: string | null;
}

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const slash = raw.indexOf('/');
  const head = slash === -1 ? raw : raw.slice(0, slash);
  const tail = slash === -1 ? '' : raw.slice(slash + 1);
  const section = (SECTIONS as readonly string[]).includes(head) ? (head as Section) : 'overview';
  const id = DETAIL_SECTIONS.has(section) && tail.length > 0 ? decodeURIComponent(tail) : null;
  return { section, id };
}

/** Build the hash href for a section (optionally a drill-down). */
function hrefFor(section: Section, id?: string | null): string {
  return id != null && id.length > 0 ? `#${section}/${encodeURIComponent(id)}` : `#${section}`;
}

/** Programmatic navigation (after an action) — assigning the hash pushes a
 *  history entry, exactly like a link click, so Back returns here. */
function go(section: Section, id?: string | null): void {
  window.location.hash = hrefFor(section, id);
}

/** Subscribe to the URL hash; re-renders on any history navigation. */
function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash());
  useEffect(() => {
    const onChange = (): void => setRoute(parseHash());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

/**
 * The one navigation primitive: a real `<a href="#…">` (so the URL is shareable
 * and the browser/mouse Back-Forward work) that also intercepts the click to
 * push the route in-SPA. Plain clicks route in-app; modified clicks
 * (cmd/ctrl/middle to open a new tab) fall through to the native href.
 */
function AdminLink({
  to,
  id,
  className,
  title,
  testId,
  current,
  children,
}: {
  to: Section;
  id?: string | null;
  className?: string;
  title?: string;
  testId?: string;
  current?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <a
      href={hrefFor(to, id)}
      className={className}
      title={title}
      aria-current={current === true ? 'page' : undefined}
      data-testid={testId}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        go(to, id);
      }}
    >
      {children}
    </a>
  );
}

export function AdminDashboard(): JSX.Element {
  const route = useHashRoute();
  const section = route.section;
  const [token, setToken] = useState<string>(getAdminSessionToken() ?? '');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [days, setDays] = useState<number>(7);
  const [analytics, setAnalytics] = useState<AnalyticsState | null>(null);
  const [fleet, setFleet] = useState<FleetState | null>(null);
  const [status, setStatus] = useState<SystemStatus | null>(null);

  async function loadStatus(): Promise<void> {
    const res = await fetchStatus();
    setStatus(res.kind === 'ok' ? res.status : null);
  }

  async function loadAnalytics(t: string, d: number): Promise<void> {
    setDays(d);
    setAnalytics({ s: 'loading' });
    const res = await fetchAdminAnalytics(t, d);
    if (res.kind === 'ok') setAnalytics({ s: 'ok', data: res.analytics });
    else
      setAnalytics({
        s: 'err',
        message:
          res.kind === 'unauthorized'
            ? 'Admin token rejected.'
            : res.kind === 'network'
              ? `Network: ${res.message}`
              : `Server returned ${String(res.status)}.`,
      });
  }

  async function loadFleet(t: string): Promise<void> {
    setFleet({ s: 'loading' });
    const res = await fetchAdminFleet(t);
    if (res.kind === 'ok') setFleet({ s: 'ok', data: res.fleet });
    else
      setFleet({
        s: 'err',
        message:
          res.kind === 'unauthorized'
            ? 'Admin token rejected.'
            : res.kind === 'network'
              ? `Network: ${res.message}`
              : `Server returned ${String(res.status)}.`,
      });
  }

  async function openTenant(id: string, quiet = false): Promise<void> {
    // Quiet refresh (after an operator action) skips the loading flash so the
    // open TenantDetailBody stays mounted and keeps its success/error message.
    if (!quiet) setDetail({ id, s: 'loading' });
    const res = await fetchTenantDetail(token, id);
    if (res.kind === 'ok') setDetail({ id, s: 'ok', detail: res.detail });
    else
      setDetail({
        id,
        s: 'err',
        message: res.kind === 'unauthorized' ? 'Admin token rejected.' : res.kind === 'not-found' ? 'Tenant not found.' : res.kind === 'network' ? `Network: ${res.message}` : `Server returned ${String(res.status)}.`,
      });
  }

  // `t` empty = authorize with the staff session cookie; non-empty = break-glass
  // bearer token. Both flow through fetchAdminOverview (credentials:'include').
  async function load(t: string): Promise<void> {
    if (t.length > 0) setAdminSessionToken(t);
    setState({ kind: 'loading' });
    const res = await fetchAdminOverview(t);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', overview: res.overview });
      void loadStatus(); // public endpoint; surfaced on the Overview
    } else {
      setState({
        kind: 'err',
        message:
          res.kind === 'unauthorized'
            ? t.length === 0
              ? 'Not signed in as staff. Sign in to the portal with a staff account, or paste a break-glass token below.'
              : 'Admin token rejected.'
            : res.kind === 'network'
              ? `Network: ${res.message}`
              : `Server returned ${String(res.status)}.`,
      });
    }
  }

  // On mount: try the staff session cookie first (no token to paste). If that
  // isn't a staff account, fall back to a stored break-glass token; otherwise
  // leave the connect panel showing.
  useEffect(() => {
    void (async () => {
      const who = await fetchAdminWhoami();
      if (who.kind === 'staff') {
        void load('');
        return;
      }
      const stored = getAdminSessionToken();
      if (stored !== null && stored.length > 0) void load(stored);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overview = state.kind === 'ok' ? state.overview : null;
  const connected = overview !== null;

  // Load whatever the current location needs, once connected. Driven by the
  // route so a deep link (or a Back/Forward step) lands on real data, not an
  // empty panel. Tenant detail lives in `detail` here; customer + device detail
  // load inside their own views from the id prop.
  const routeKey = `${section}/${route.id ?? ''}`;
  useEffect(() => {
    if (!connected) return;
    if (section === 'analytics' && analytics === null) void loadAnalytics(token, days);
    else if (section === 'fleet' && fleet === null) void loadFleet(token);
    if (section === 'tenants' && route.id !== null) void openTenant(route.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, connected]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar variant="dense">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Box component="span" aria-hidden sx={{ color: 'secondary.main', fontSize: 20 }}>▦</Box>
            <Typography variant="subtitle1" fontWeight={700}>MobiLauncher</Typography>
          </Box>
          {connected && (
            <Tooltip title="Refresh">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                  if (section === 'analytics') void loadAnalytics(token, days);
                  else if (section === 'fleet') void loadFleet(token);
                  else void load(token);
                }}
                data-testid="admin-refresh"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <Chip label="Admin" size="small" color="secondary" variant="outlined" sx={{ ml: 1 }} />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' } }}
      >
        <Toolbar variant="dense" />
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {NAV.map((grp) => (
            <List
              key={grp.group}
              dense
              subheader={<ListSubheader component="div" sx={{ bgcolor: 'transparent' }}>{grp.group}</ListSubheader>}
            >
              {grp.items.map((item) => (
                <ListItemButton
                  key={item.id}
                  component="a"
                  href={hrefFor(item.id)}
                  disabled={!connected}
                  selected={section === item.id}
                  onClick={(e) => {
                    if (!connected) return;
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    go(item.id);
                  }}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          ))}
        </Box>
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FiberManualRecordIcon sx={{ fontSize: 12, color: connected ? 'success.main' : 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">{connected ? 'Connected' : 'Not connected'}</Typography>
          </Stack>
          {token.length > 0 && (
            <Button
              size="small"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={() => {
                setAdminSessionToken('');
                setToken('');
                setState({ kind: 'idle' });
                setDetail(null);
                setAnalytics(null);
                setFleet(null);
                go('overview');
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              Forget token
            </Button>
          )}
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Toolbar variant="dense" />
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          {overview === null ? (
            <ConnectPanel
              token={token}
              onTokenChange={setToken}
              loading={state.kind === 'loading'}
              error={state.kind === 'err' ? state.message : null}
              onLoad={() => void load(token)}
            />
          ) : section === 'analytics' ? (
            <AnalyticsSection
              state={analytics}
              days={days}
              onDays={(d) => void loadAnalytics(token, d)}
            />
          ) : section === 'fleet' ? (
            <FleetSection
              state={fleet}
              token={token}
              deviceId={route.id}
              onChanged={() => {
                void loadFleet(token);
                void load(token);
              }}
            />
          ) : section === 'tenants' ? (
            route.id !== null ? (
              <TenantDetailView
                state={detail ?? { id: route.id, s: 'loading' }}
                token={token}
                customers={overview.customers}
                onRefresh={() => void openTenant(route.id!, true)}
                onDeleted={() => {
                  go('tenants');
                  void load(token);
                }}
              />
            ) : (
              <TenantsSection
                tenants={overview.tenants}
                customers={overview.customers}
                token={token}
                onChanged={() => void load(token)}
              />
            )
          ) : section === 'customers' ? (
            <CustomersSection
              customers={overview.customers}
              token={token}
              customerId={route.id}
              onChanged={() => void load(token)}
            />
          ) : (
            <OverviewSection
              overview={overview}
              status={status}
              token={token}
              onReload={() => void load(token)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function ConnectPanel({
  token,
  onTokenChange,
  loading,
  error,
  onLoad,
}: {
  token: string;
  onTokenChange: (v: string) => void;
  loading: boolean;
  error: string | null;
  onLoad: () => void;
}): JSX.Element {
  return (
    <Box sx={{ maxWidth: 460, mx: 'auto', mt: 4 }} data-testid="admin-dashboard">
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Connect</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Sign in to the portal with a <strong>staff account</strong> and this console loads
            automatically — no token needed. As a fallback, paste a break-glass admin token below
            (stored in this browser only).
          </Typography>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); onLoad(); }}>
            <TextField
              type="password"
              label="Admin bearer token"
              value={token}
              onChange={(e) => onTokenChange(e.target.value)}
              placeholder="paste your ADMIN_BEARER_TOKEN here"
              fullWidth
              size="small"
              inputProps={{ spellCheck: false, autoComplete: 'off', 'data-testid': 'admin-dashboard-token', style: { fontFamily: 'monospace' } }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || token.length === 0}
              data-testid="admin-dashboard-load"
              sx={{ mt: 2 }}
            >
              {loading ? 'Loading…' : 'Load overview'}
            </Button>
            {error !== null && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function OverviewSection({
  overview,
  status,
  token,
  onReload,
}: {
  overview: AdminOverview;
  status: SystemStatus | null;
  token: string;
  onReload: () => void;
}): JSX.Element {
  const { counts, deltas, mrr, tierMix, tenants, customers } = overview;
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<ActionMsg>(null);
  const [revokeId, setRevokeId] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeMsg, setRevokeMsg] = useState<ActionMsg>(null);

  /**
   * Revoke a specific license by id — the leaked-license.json case: you have
   * the id (from a security report, or the tenant's License history panel) but
   * don't want to disable the whole tenant. The revocation list is keyed on
   * this id, so devices honor it on their next revocation check or reboot.
   */
  async function revokeById(): Promise<void> {
    const id = revokeId.trim();
    if (id.length === 0) return;
    const ok = await confirmDialog({
      title: 'Revoke license',
      message: `Revoke license ${id}? Any device running on it drops to Core on its next revocation check. This can't be undone here.`,
      destructive: true,
      confirmLabel: 'Revoke',
    });
    if (!ok) return;
    setRevoking(true);
    setRevokeMsg(null);
    const res = await adminRevokeLicense(token, id);
    setRevoking(false);
    if (res.kind === 'ok') {
      const body = res.body as { alreadyRevoked?: boolean };
      setRevokeMsg({ kind: 'ok', text: body.alreadyRevoked ? 'Already revoked.' : 'License revoked.' });
      setRevokeId('');
      onReload();
    } else if (res.kind === 'not-found') {
      setRevokeMsg({ kind: 'err', text: 'No license with that id.' });
    } else {
      setRevokeMsg(actionMessage(res, ''));
    }
  }

  async function backfillBilling(): Promise<void> {
    setSyncing(true);
    setSyncMsg(null);
    const res = await adminBackfillBilling(token);
    setSyncing(false);
    if (res.kind === 'ok') {
      const body = res.body as { captured?: number; scanned?: number };
      setSyncMsg({
        kind: 'ok',
        text: `Synced ${String(body.captured ?? 0)} of ${String(body.scanned ?? 0)} subscriptions.`,
      });
      onReload();
    } else {
      setSyncMsg(actionMessage(res, ''));
    }
  }

  const uncovered = mrr !== null ? mrr.totalPaidSubs - mrr.pricedSubs : 0;
  const growth = cumulativeByMonth(customers.map((c) => c.createdAt));
  const fleets = tenants
    .filter((t) => t.devices > 0)
    .sort((a, b) => b.devices - a.devices)
    .slice(0, 8)
    .map((t) => ({ label: t.tenantId, total: t.devices, active: t.activeDevices }));
  const segments = [
    { label: 'Enterprise', value: tierMix.enterprise, color: '#03dac6' },
    { label: 'Pro', value: tierMix.pro, color: '#1976d2' },
    { label: 'Core', value: tierMix.core, color: 'rgba(232,234,237,0.32)' },
  ].filter((s) => s.value > 0);

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }} data-testid="admin-cards">
        <Stat label="Customers" value={String(counts.customers)} icon={<PeopleIcon />} delta={deltas?.customers} />
        <Stat label="Tenants" value={String(counts.tenants)} icon={<StorageIcon />} delta={deltas?.tenants} />
        <Stat
          label="Devices"
          value={`${String(counts.activeDevices)} / ${String(counts.devices)}`}
          sub="active / total"
          icon={<GridViewIcon />}
          delta={deltas?.devices}
        />
        <Stat
          label="Tier mix"
          value={`${String(tierMix.enterprise)} · ${String(tierMix.pro)} · ${String(tierMix.core)}`}
          sub="ent · pro · core"
          icon={<VpnKeyIcon />}
        />
        {overview.mrr !== null && (
          <Stat
            label="MRR"
            value={`${formatUsd(overview.mrr.monthlyCents)}/mo`}
            sub={
              overview.mrr.pricedSubs === overview.mrr.totalPaidSubs
                ? `${String(overview.mrr.totalPaidSubs)} paid subs`
                : `${String(overview.mrr.pricedSubs)} of ${String(overview.mrr.totalPaidSubs)} subs priced`
            }
            icon={<BarChartIcon />}
          />
        )}
      </Box>

      {mrr !== null && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {uncovered > 0
              ? `${String(uncovered)} paid subscription${uncovered === 1 ? '' : 's'} awaiting a price.`
              : 'All paid subscriptions priced.'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={syncing}
            onClick={() => void backfillBilling()}
            data-testid="admin-billing-sync"
          >
            {syncing ? 'Syncing…' : 'Sync billing from Stripe'}
          </Button>
          {syncMsg !== null && (
            <Typography variant="body2" color={syncMsg.kind === 'ok' ? 'success.main' : 'error'} role="status">
              {syncMsg.text}
            </Typography>
          )}
        </Stack>
      )}

      {status !== null && <SystemStatusPanel status={status} />}

      <Panel title="Revoke a license by id">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="license id (UUID)"
            value={revokeId}
            onChange={(e) => setRevokeId(e.target.value)}
            inputProps={{ 'data-testid': 'admin-revoke-id-input', spellCheck: false, style: { fontFamily: 'monospace' } }}
            sx={{ minWidth: 340 }}
          />
          <Button
            size="small"
            color="error"
            variant="outlined"
            disabled={revoking || revokeId.trim().length === 0}
            onClick={() => void revokeById()}
            data-testid="admin-revoke-id-submit"
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
          {revokeMsg !== null && (
            <Typography variant="body2" color={revokeMsg.kind === 'ok' ? 'success.main' : 'error'} role="status">
              {revokeMsg.text}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Kills a specific leaked license.json without disabling the tenant. Devices honor it on their next
          revocation check (~hourly) or reboot.
        </Typography>
      </Panel>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {segments.length > 0 && (
          <Panel title="Tier mix">
            <Donut segments={segments} total={counts.tenants} centerLabel="tenants" />
          </Panel>
        )}
        {/* Only meaningful with at least two months of history. */}
        {growth.length >= 2 && (
          <Panel title="Customer growth">
            <LineChart
              height={220}
              xAxis={[{ scaleType: 'point', data: growth.map((p) => p.label) }]}
              series={[{ data: growth.map((p) => p.count), area: true, color: '#1976d2', label: 'Customers' }]}
              margin={{ left: 40, right: 16, top: 16, bottom: 24 }}
            />
          </Panel>
        )}
      </Box>

      {fleets.length > 0 && (
        <Panel title="Devices by tenant">
          <BarChart
            layout="horizontal"
            height={Math.max(160, fleets.length * 46)}
            yAxis={[{ scaleType: 'band', data: fleets.map((f) => f.label) }]}
            series={[
              { data: fleets.map((f) => f.active), label: 'Active (15 min)', color: '#03dac6' },
              { data: fleets.map((f) => f.total), label: 'Total', color: 'rgba(232,234,237,0.32)' },
            ]}
            margin={{ left: 96, right: 16, top: 16, bottom: 24 }}
          />
        </Panel>
      )}
    </Stack>
  );
}

const STATUS_META: Record<SystemStatusLevel, { label: string; color: string }> = {
  operational: { label: 'Operational', color: '#4ade80' },
  degraded: { label: 'Degraded', color: '#f0b429' },
  'partial-outage': { label: 'Partial outage', color: '#ff9f43' },
  'major-outage': { label: 'Major outage', color: '#ff6b6b' },
};
const STATUS_RANK: Record<SystemStatusLevel, number> = {
  operational: 0,
  degraded: 1,
  'partial-outage': 2,
  'major-outage': 3,
};
const COMPONENT_LABELS: Record<string, string> = {
  marketing: 'Marketing site',
  launcher: 'Launcher app',
  api: 'Licensing API',
  bundles: 'Hosted bundles',
  status: 'Status page',
};

/** The 5-min uptime monitor, surfaced on the Overview. */
function SystemStatusPanel({ status }: { status: SystemStatus }): JSX.Element {
  const worst = status.results.reduce<SystemStatusLevel>(
    (acc, r) => (STATUS_RANK[r.status] > STATUS_RANK[acc] ? r.status : acc),
    'operational',
  );
  const overall = status.results.length > 0 ? STATUS_META[worst] : null;
  return (
    <Panel title="System status">
      <Box data-testid="admin-status">
        {overall !== null && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <FiberManualRecordIcon sx={{ fontSize: 12, color: overall.color }} />
            <Typography fontWeight={600}>{worst === 'operational' ? 'All systems operational' : overall.label}</Typography>
            <Typography variant="body2" color="text.secondary">· updated {relativeTime(status.updatedAt)}</Typography>
          </Stack>
        )}
        {status.results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No probe data yet — the monitor runs every 5 minutes.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {status.results.map((r) => {
              const meta = STATUS_META[r.status];
              return (
                <Stack key={r.id} direction="row" spacing={1} alignItems="center">
                  <FiberManualRecordIcon sx={{ fontSize: 10, color: meta.color }} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{COMPONENT_LABELS[r.id] ?? r.id}</Typography>
                  <Typography variant="body2" color="text.secondary">{meta.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 90, textAlign: 'right' }}>
                    {r.httpStatus > 0 ? `${String(r.latencyMs)}ms` : 'unreachable'}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Panel>
  );
}

/** "2m ago" style relative time from an ISO timestamp. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${String(secs)}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${String(hrs)}h ago`;
  return `${String(Math.round(hrs / 24))}d ago`;
}

function Panel({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

/** Donut (PieChart) with a centered total + a label/value legend below. */
function Donut({
  segments,
  total,
  centerLabel,
}: {
  segments: { label: string; value: number; color: string }[];
  total: number;
  centerLabel: string;
}): JSX.Element {
  return (
    <Box>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <PieChart
          height={200}
          width={200}
          series={[
            {
              innerRadius: 56,
              outerRadius: 90,
              paddingAngle: 1.5,
              cornerRadius: 2,
              data: segments.map((s, i) => ({ id: i, value: s.value, label: s.label, color: s.color })),
            },
          ]}
          margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
          slotProps={{ legend: { hidden: true } }}
        />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>{String(total)}</Typography>
          <Typography variant="caption" color="text.secondary">{centerLabel}</Typography>
        </Box>
      </Box>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {segments.map((s) => (
          <Stack key={s.label} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{s.label}</Typography>
            <Typography variant="body2" color="text.secondary">{String(s.value)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** Bucket unix-second timestamps into cumulative monthly counts (oldest first). */
function cumulativeByMonth(timestamps: number[]): { label: string; count: number }[] {
  const byMonth = new Map<string, number>();
  for (const ts of timestamps) {
    const d = new Date(ts * 1000);
    const key = `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
  }
  let cumulative = 0;
  return [...byMonth.keys()]
    .sort()
    .map((key) => {
      cumulative += byMonth.get(key) ?? 0;
      return { label: key, count: cumulative };
    });
}

/** Native MUI select for admin table filters — native keeps the selectOptions
 *  tests working; MUI-styled to match the rest of the console. */
function FilterSelect({
  label,
  value,
  onChange,
  testId,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId: string;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ native: true, inputProps: { 'data-testid': testId, 'aria-label': label } }}
      sx={{ minWidth: 150 }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </TextField>
  );
}

/** Search box for the admin tables (preserves the admin-search testid). */
function AdminSearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <TextField
      size="small"
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputProps={{ spellCheck: false, 'data-testid': 'admin-search' }}
      sx={{ minWidth: 220 }}
    />
  );
}

function TenantsSection({
  tenants,
  customers,
  token,
  onChanged,
}: {
  tenants: AdminOverview['tenants'];
  customers: AdminOverview['customers'];
  token: string;
  onChanged: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [fTier, setFTier] = useState<'' | 'core' | 'pro' | 'enterprise'>('');

  const q = query.trim().toLowerCase();
  const rows = tenants
    .filter((t) => fTier === '' || t.tier === fTier)
    .filter(
      (t) =>
        q === '' ||
        t.tenantId.toLowerCase().includes(q) ||
        (t.ownerEmail ?? '').toLowerCase().includes(q),
    );

  const columns: GridColDef<AdminOverview['tenants'][number]>[] = [
    {
      field: 'tenantId', headerName: 'Tenant', flex: 1, minWidth: 160,
      renderCell: (p) => (
        <AdminLink to="tenants" id={String(p.value)} title="View tenant detail">
          <Box component="code" sx={{ fontFamily: 'monospace', color: 'primary.light' }}>{String(p.value)}</Box>
        </AdminLink>
      ),
    },
    {
      field: 'ownerEmail', headerName: 'Owner', flex: 1, minWidth: 180,
      renderCell: (p) => (p.value ? String(p.value) : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>),
    },
    {
      field: 'tier', headerName: 'Tier', width: 130,
      renderCell: (p) => <Chip size="small" label={String(p.value)} color={p.value === 'enterprise' ? 'secondary' : p.value === 'pro' ? 'primary' : 'default'} />,
    },
    {
      field: 'devices', headerName: 'Devices', width: 120,
      renderCell: (p) => `${String(p.row.activeDevices)} / ${String(p.row.devices)}`,
    },
    {
      field: 'updatedAt', headerName: 'Updated', width: 140,
      renderCell: (p) => formatDate(Number(p.value)),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography variant="h5" fontWeight={700}>
            Tenants ({String(rows.length)}{rows.length !== tenants.length ? ` of ${String(tenants.length)}` : ''})
          </Typography>
          <FilterSelect
            label="Filter by tier"
            value={fTier}
            onChange={(v) => setFTier(v as '' | 'core' | 'pro' | 'enterprise')}
            testId="admin-tenant-ftier"
            options={[{ value: '', label: 'Any tier' }, { value: 'core', label: 'Core' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }]}
          />
          <AdminSearchField value={query} onChange={setQuery} placeholder="Search tenant or owner…" />
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={creating ? 'Close' : 'New tenant'}>
            <IconButton onClick={() => setCreating((v) => !v)} data-testid="admin-tenant-new-toggle" aria-label={creating ? 'Close' : 'New tenant'}>
              {creating ? <ClearIcon /> : <AddIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Export CSV">
            <IconButton
              data-testid="admin-export"
              aria-label="Export CSV"
              onClick={() =>
                downloadCsv(
                  'tenants.csv',
                  ['Tenant', 'Owner', 'Tier', 'Active devices', 'Total devices', 'Updated'],
                  rows.map((t) => [t.tenantId, t.ownerEmail ?? '', t.tier, t.activeDevices, t.devices, formatDate(t.updatedAt)]),
                )
              }
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={creating}>
        <Box sx={{ mb: 2 }}>
          <NewTenantForm
            token={token}
            customers={customers}
            onDone={() => { setCreating(false); onChanged(); }}
            onCancel={() => setCreating(false)}
          />
        </Box>
      </Collapse>

      <Box data-testid="admin-tenants" sx={{ flexGrow: 1, minHeight: 360, minWidth: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.tenantId}
          density="compact"
          disableRowSelectionOnClick
          initialState={{ sorting: { sortModel: [{ field: 'devices', sort: 'desc' }] }, pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
}

function NewTenantForm({
  token,
  customers,
  onDone,
  onCancel,
}: {
  token: string;
  customers: AdminOverview['customers'];
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [ownerCustomerId, setOwnerCustomerId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = ownerCustomerId !== '' && name.trim().length > 0 && !busy;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    const res = await adminCreateTenant(token, ownerCustomerId, name.trim());
    setBusy(false);
    if (res.kind === 'ok') onDone();
    else setErr(actionMessage(res, '').text);
  }

  return (
    <Card variant="outlined" component="form" data-testid="admin-tenant-new" onSubmit={(e) => void submit(e)}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Owner"
            value={ownerCustomerId}
            onChange={(e) => setOwnerCustomerId(e.target.value)}
            size="small"
            fullWidth
            SelectProps={{ native: true, inputProps: { 'data-testid': 'admin-tenant-new-owner' } }}
          >
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.email}</option>
            ))}
          </TextField>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            placeholder="Acme Corp"
            inputProps={{ maxLength: 80, 'data-testid': 'admin-tenant-new-name' }}
          />
        </Stack>
        {err !== null && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" size="small" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create tenant'}
          </Button>
          <Button type="button" size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Breadcrumb back-link for the detail views — a real hash anchor (Back/Forward
 *  work) styled as a small MUI button. */
function BackLink({ to, children }: { to: Section; children: ReactNode }): JSX.Element {
  return (
    <Button
      component="a"
      href={hrefFor(to)}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        go(to);
      }}
      size="small"
      color="inherit"
      sx={{ mb: 2, pl: 0 }}
    >
      {children}
    </Button>
  );
}

function TenantDetailView({
  state,
  token,
  customers,
  onRefresh,
  onDeleted,
}: {
  state: DetailState;
  token: string;
  customers: AdminOverview['customers'];
  onRefresh: () => void;
  onDeleted: () => void;
}): JSX.Element {
  return (
    <Box data-testid="admin-tenant-detail">
      <BackLink to="tenants">← All tenants</BackLink>
      {state.s === 'loading' ? (
        <Typography color="text.secondary">Loading {state.id}…</Typography>
      ) : state.s === 'err' ? (
        <Alert severity="error">{state.message}</Alert>
      ) : (
        <TenantDetailBody
          d={state.detail}
          token={token}
          customers={customers}
          onRefresh={onRefresh}
          onDeleted={onDeleted}
        />
      )}
    </Box>
  );
}

type ActionMsg = { kind: 'ok' | 'err'; text: string } | null;

function actionMessage(res: AdminActionResult, okText: string): NonNullable<ActionMsg> {
  switch (res.kind) {
    case 'ok':
      return { kind: 'ok', text: okText };
    case 'unauthorized':
      return { kind: 'err', text: 'Admin token rejected.' };
    case 'not-found':
      return { kind: 'err', text: 'Not found — it may already be gone.' };
    case 'invalid':
      return { kind: 'err', text: res.message };
    case 'network':
      return { kind: 'err', text: `Network: ${res.message}` };
    default:
      return { kind: 'err', text: `Server returned ${String(res.status)}.` };
  }
}


function TenantDetailBody({
  d,
  token,
  customers,
  onRefresh,
  onDeleted,
}: {
  d: AdminTenantDetail;
  token: string;
  customers: AdminOverview['customers'];
  onRefresh: () => void;
  onDeleted: () => void;
}): JSX.Element {
  // Which action is in flight (by a unique key) — disables every button while
  // one runs. `msg` survives the quiet refresh because this component stays
  // mounted (the detail state never flips to 'loading' on refresh).
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<ActionMsg>(null);
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState(d.tenant.deploymentName ?? '');
  const [ownerId, setOwnerId] = useState(d.tenant.ownerCustomerId ?? '');
  const tid = d.tenant.tenantId;

  async function run(
    key: string,
    okText: string,
    fn: () => Promise<AdminActionResult>,
  ): Promise<boolean> {
    setBusy(key);
    setMsg(null);
    const res = await fn();
    setBusy(null);
    setMsg(actionMessage(res, okText));
    if (res.kind === 'ok') onRefresh();
    return res.kind === 'ok';
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{tid}</Box>
        <Chip size="small" label={d.tenant.tier} color={d.tenant.tier === 'enterprise' ? 'secondary' : d.tenant.tier === 'pro' ? 'primary' : 'default'} />
        <Typography variant="body2" color="text.secondary">
          {d.tenant.ownerEmail ?? 'no owner'} · updated {formatDate(d.tenant.updatedAt)}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={busy !== null}
          onClick={() => void run('reseed', 'Tenant defaults reseeded.', () => adminReseedDefaults(token, tid))}
          data-testid="admin-action-reseed"
          sx={{ ml: 'auto' }}
        >
          {busy === 'reseed' ? 'Reseeding…' : 'Reseed defaults'}
        </Button>
      </Stack>

      {msg !== null && <Alert severity={msg.kind === 'ok' ? 'success' : 'error'} role="status">{msg.text}</Alert>}

      <Panel title="Manage">
        <Stack spacing={2} data-testid="admin-tenant-manage">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Deployment name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth placeholder="Your launcher" inputProps={{ maxLength: 80, 'data-testid': 'admin-tenant-name' }} />
            <TextField select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} size="small" fullWidth SelectProps={{ native: true, inputProps: { 'data-testid': 'admin-tenant-owner' } }}>
              {d.tenant.ownerCustomerId === null && <option value="">No owner</option>}
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.email}</option>))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={busy !== null || name.trim().length === 0 || name.trim() === (d.tenant.deploymentName ?? '')} onClick={() => void run('rename', 'Tenant renamed.', () => adminUpdateTenant(token, tid, { deploymentName: name.trim() }))} data-testid="admin-tenant-rename">
              {busy === 'rename' ? 'Saving…' : 'Rename'}
            </Button>
            <Button size="small" variant="outlined" disabled={busy !== null || ownerId === '' || ownerId === (d.tenant.ownerCustomerId ?? '')} onClick={() => void run('reassign', 'Owner reassigned.', () => adminUpdateTenant(token, tid, { ownerCustomerId: ownerId }))} data-testid="admin-tenant-reassign">
              {busy === 'reassign' ? 'Saving…' : 'Reassign owner'}
            </Button>
          </Stack>
        </Stack>
      </Panel>

      <Panel title={`License history (${String(d.licenses.length)})`}>
        {d.licenses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No licenses issued — running as Core.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>License ID</TableCell><TableCell>Tier</TableCell><TableCell>Status</TableCell><TableCell>Expires</TableCell><TableCell>Issued</TableCell><TableCell>Stripe</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {d.licenses.map((l) => (
                <TableRow key={l.id}>
                  {/* The license id is what the revocation list is keyed on —
                      show it (copyable) so it can be matched to a device's
                      reported license or pasted into "Revoke by id". */}
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', userSelect: 'all' }} title={l.id}>{l.id}</TableCell>
                  <TableCell><Chip size="small" label={l.tier} color={l.tier === 'enterprise' ? 'secondary' : l.tier === 'pro' ? 'primary' : 'default'} /></TableCell>
                  <TableCell {...(l.status === 'revoked' ? { sx: { color: 'error.main', fontWeight: 600 } } : {})}>{l.status}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{l.expiresAt === null ? 'never' : formatDate(l.expiresAt)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatDate(l.createdAt)}</TableCell>
                  <TableCell>{l.stripeLinked ? 'yes' : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                  <TableCell>
                    {l.status === 'active' ? (
                      <Button
                        size="small"
                        color="error"
                        disabled={busy !== null}
                        onClick={() => {
                          void (async () => {
                            const ok = await confirmDialog({ title: 'Revoke license', message: `Revoke this ${l.tier} license? Fleets fall back to Core on their next license check.`, destructive: true, confirmLabel: 'Revoke' });
                            if (!ok) return;
                            void run(`revoke-${l.id}`, 'License revoked.', () => adminRevokeLicense(token, l.id));
                          })();
                        }}
                      >
                        {busy === `revoke-${l.id}` ? 'Revoking…' : 'Revoke'}
                      </Button>
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title={`Devices (${String(d.devices.length)})`}>
        {d.devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No devices have registered yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Device</TableCell><TableCell>OS</TableCell><TableCell>LAN IP</TableCell><TableCell>Boots</TableCell><TableCell>Last seen</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {d.devices.map((dev) => (
                <TableRow key={dev.id}>
                  <TableCell>{dev.deviceName ?? <Box component="code" sx={{ fontFamily: 'monospace' }}>{dev.id.slice(0, 8)}…</Box>}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{dev.osVersion ?? '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{dev.localIpv4 ?? '—'}</TableCell>
                  <TableCell>{String(dev.bootCount)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatDateTime(dev.lastSeenAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title={`Broadcasts (${String(d.broadcasts.length)})`}>
        {!composing && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setComposing(true)} data-testid="admin-broadcast-new" sx={{ mb: 2 }}>
            New broadcast
          </Button>
        )}
        {composing && (
          <Box sx={{ mb: 2 }}>
            <BroadcastComposer
              busy={busy === 'broadcast'}
              onCancel={() => setComposing(false)}
              onCreate={async (input) => {
                const ok = await run('broadcast', 'Broadcast posted.', () => adminCreateBroadcast(token, tid, input));
                if (ok) setComposing(false);
                return ok;
              }}
            />
          </Box>
        )}
        {d.broadcasts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No broadcasts posted.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Title</TableCell><TableCell>Severity</TableCell><TableCell>Window</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {d.broadcasts.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{b.title}</TableCell>
                  <TableCell>{b.severity}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatDate(b.startsAt)} → {formatDate(b.expiresAt)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      disabled={busy !== null}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirmDialog({ title: 'Delete broadcast', message: 'Delete this broadcast? It disappears from the fleet immediately.', destructive: true, confirmLabel: 'Delete' });
                          if (!ok) return;
                          void run(`del-${b.id}`, 'Broadcast deleted.', () => adminDeleteBroadcast(token, b.id));
                        })();
                      }}
                    >
                      {busy === `del-${b.id}` ? 'Deleting…' : 'Delete'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title={`Audit log (${String(d.audit.length)})`}>
        {d.audit.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No privileged events recorded (Pro/Enterprise only).</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Event</TableCell><TableCell>Detail</TableCell><TableCell>When</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {d.audit.map((e) => (
                <TableRow key={String(e.id)}>
                  <TableCell>{AUDIT_LABELS[e.name] ?? e.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{auditDetail(e.attrs)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{formatMsDateTime(e.receivedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title="Danger zone">
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={busy !== null}
          onClick={() => {
            void (async () => {
              const ok = await confirmDialog({
                title: 'Delete tenant',
                message: `Permanently delete ${tid}? This removes its config, licenses, devices, broadcasts, and telemetry. This cannot be undone.`,
                destructive: true,
                confirmLabel: 'Delete tenant',
                requireText: tid,
              });
              if (!ok) return;
              if (await run('delete', 'Tenant deleted.', () => adminDeleteTenant(token, tid))) {
                onDeleted();
              }
            })();
          }}
          data-testid="admin-tenant-delete"
        >
          Delete tenant
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Permanently removes this fleet and all its licenses, devices, broadcasts, and telemetry.
        </Typography>
      </Panel>
    </Stack>
  );
}

/** Human labels for the privileged events emitted via engine.audit. */
const AUDIT_LABELS: Record<string, string> = {
  'admin.exitGesture': 'Exit gesture',
  'admin.pinAccepted': 'PIN accepted',
  'admin.pinRejected': 'PIN rejected',
  'admin.overlayOpened': 'Admin overlay opened',
  'admin.overlayClosed': 'Admin overlay closed',
};

/** Compact one-line view of an event's JSON attrs (or em-dash when none). */
function auditDetail(attrs: string | null): ReactNode {
  if (attrs === null || attrs.trim() === '' || attrs.trim() === '{}') {
    return em();
  }
  return attrs.length > 80 ? `${attrs.slice(0, 80)}…` : attrs;
}

const BROADCAST_EXPIRY_OPTIONS = [1, 7, 30];

function BroadcastComposer({
  busy,
  onCreate,
  onCancel,
}: {
  busy: boolean;
  onCreate: (input: AdminBroadcastInput) => Promise<boolean>;
  onCancel: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('info');
  const [days, setDays] = useState(7);
  const [dismissible, setDismissible] = useState(true);

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !busy;

  return (
    <Card
      variant="outlined"
      component="form"
      data-testid="admin-broadcast-form"
      onSubmit={(e) => {
        e.preventDefault();
        const expiresAt = Math.floor(Date.now() / 1000) + days * 86_400;
        void onCreate({ title: title.trim(), body: body.trim(), severity, expiresAt, dismissible });
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth placeholder="Scheduled maintenance tonight" inputProps={{ maxLength: 80 }} />
          <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} size="small" fullWidth multiline rows={3} placeholder="Kiosks may briefly restart between 1–2am." inputProps={{ maxLength: 500 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField select label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value as 'info' | 'warning' | 'critical')} size="small" SelectProps={{ native: true }} sx={{ minWidth: 140 }}>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </TextField>
            <TextField select label="Expires in" value={String(days)} onChange={(e) => setDays(Number(e.target.value))} size="small" SelectProps={{ native: true }} sx={{ minWidth: 140 }}>
              {BROADCAST_EXPIRY_OPTIONS.map((d) => (<option key={d} value={String(d)}>{String(d)} {d === 1 ? 'day' : 'days'}</option>))}
            </TextField>
            <FormControlLabel control={<Checkbox checked={dismissible} onChange={(e) => setDismissible(e.target.checked)} />} label="Dismissible" />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" size="small" disabled={!canSubmit}>{busy ? 'Posting…' : 'Post broadcast'}</Button>
            <Button type="button" size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CustomersSection({
  customers,
  token,
  customerId,
  onChanged,
}: {
  customers: AdminOverview['customers'];
  token: string;
  /** When set (from the `#customers/:id` route), show that customer's detail. */
  customerId: string | null;
  onChanged: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [fStripe, setFStripe] = useState<'' | 'linked' | 'unlinked'>('');
  const [fFleet, setFFleet] = useState<'' | 'has' | 'none'>('');

  if (customerId !== null) {
    return (
      <CustomerDetailView
        customerId={customerId}
        token={token}
        onChanged={onChanged}
        onDeleted={() => {
          go('customers');
          onChanged();
        }}
      />
    );
  }

  const q = query.trim().toLowerCase();
  const rows = customers
    .filter((c) => fStripe === '' || (fStripe === 'linked' ? c.stripeLinked : !c.stripeLinked))
    .filter((c) => fFleet === '' || (fFleet === 'has' ? c.tenantCount > 0 : c.tenantCount === 0))
    .filter((c) => q === '' || c.email.toLowerCase().includes(q));

  const columns: GridColDef<AdminOverview['customers'][number]>[] = [
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
    {
      field: 'stripeLinked', headerName: 'Stripe', width: 120,
      renderCell: (p) => (p.value ? <Chip size="small" label="linked" color="primary" variant="outlined" /> : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>),
    },
    { field: 'tenantCount', headerName: 'Tenants', width: 110 },
    { field: 'createdAt', headerName: 'Joined', width: 140, renderCell: (p) => formatDate(Number(p.value)) },
    {
      field: 'actions', headerName: '', width: 120, sortable: false, filterable: false,
      renderCell: (p) => (
        <AdminLink to="customers" id={p.row.id} testId={`admin-customer-manage-${p.row.id}`}>
          <Box component="span" sx={{ color: 'primary.light' }}>Manage</Box>
        </AdminLink>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography variant="h5" fontWeight={700}>
            Customers ({String(rows.length)}{rows.length !== customers.length ? ` of ${String(customers.length)}` : ''})
          </Typography>
          <FilterSelect
            label="Filter by Stripe link"
            value={fStripe}
            onChange={(v) => setFStripe(v as '' | 'linked' | 'unlinked')}
            testId="admin-customer-fstripe"
            options={[{ value: '', label: 'Any billing' }, { value: 'linked', label: 'Stripe linked' }, { value: 'unlinked', label: 'Not linked' }]}
          />
          <FilterSelect
            label="Filter by fleet ownership"
            value={fFleet}
            onChange={(v) => setFFleet(v as '' | 'has' | 'none')}
            testId="admin-customer-ffleet"
            options={[{ value: '', label: 'Any fleets' }, { value: 'has', label: 'Has a fleet' }, { value: 'none', label: 'No fleet' }]}
          />
          <AdminSearchField value={query} onChange={setQuery} placeholder="Search email…" />
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={creating ? 'Close' : 'New customer'}>
            <IconButton onClick={() => setCreating((v) => !v)} data-testid="admin-customer-new-toggle" aria-label={creating ? 'Close' : 'New customer'}>
              {creating ? <ClearIcon /> : <AddIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Export CSV">
            <IconButton
              data-testid="admin-export"
              aria-label="Export CSV"
              onClick={() =>
                downloadCsv(
                  'customers.csv',
                  ['Email', 'Stripe', 'Tenants', 'Joined'],
                  rows.map((c) => [c.email, c.stripeLinked ? 'linked' : '', c.tenantCount, formatDate(c.createdAt)]),
                )
              }
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={creating}>
        <Box sx={{ mb: 2 }}>
          <NewCustomerForm
            token={token}
            onDone={() => { setCreating(false); onChanged(); }}
            onCancel={() => setCreating(false)}
          />
        </Box>
      </Collapse>

      <Box data-testid="admin-customers" sx={{ flexGrow: 1, minHeight: 360, minWidth: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          density="compact"
          disableRowSelectionOnClick
          initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] }, pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
}

function NewCustomerForm({
  token,
  onDone,
  onCancel,
}: {
  token: string;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (email.trim().length === 0) return;
    setBusy(true);
    setErr(null);
    const res = await adminCreateCustomer(token, email.trim());
    setBusy(false);
    if (res.kind === 'ok') onDone();
    else setErr(actionMessage(res, '').text);
  }

  return (
    <Card variant="outlined" component="form" data-testid="admin-customer-new" onSubmit={(e) => void submit(e)}>
      <CardContent>
        <TextField
          type="email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
          fullWidth
          placeholder="person@company.com"
          inputProps={{ maxLength: 200, 'data-testid': 'admin-customer-new-email' }}
        />
        {err !== null && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" size="small" disabled={busy || email.trim().length === 0}>
            {busy ? 'Creating…' : 'Create customer'}
          </Button>
          <Button type="button" size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

type CustomerDetailState =
  | { s: 'loading' }
  | { s: 'ok'; detail: AdminCustomerDetail }
  | { s: 'err'; message: string };

function CustomerDetailView({
  customerId,
  token,
  onChanged,
  onDeleted,
}: {
  customerId: string;
  token: string;
  onChanged: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [state, setState] = useState<CustomerDetailState>({ s: 'loading' });

  async function load(): Promise<void> {
    setState({ s: 'loading' });
    const res = await fetchAdminCustomer(token, customerId);
    if (res.kind === 'ok') setState({ s: 'ok', detail: res.customer });
    else
      setState({
        s: 'err',
        message:
          res.kind === 'unauthorized'
            ? 'Admin token rejected.'
            : res.kind === 'not-found'
              ? 'Customer not found.'
              : res.kind === 'network'
                ? `Network: ${res.message}`
                : `Server returned ${String(res.status)}.`,
      });
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return (
    <Box data-testid="admin-customer-detail">
      <BackLink to="customers">← All customers</BackLink>
      {state.s === 'loading' ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : state.s === 'err' ? (
        <Alert severity="error">{state.message}</Alert>
      ) : (
        <CustomerDetailBody
          d={state.detail}
          token={token}
          onRefresh={() => {
            void load();
            onChanged();
          }}
          onDeleted={onDeleted}
        />
      )}
    </Box>
  );
}

function CustomerDetailBody({
  d,
  token,
  onRefresh,
  onDeleted,
}: {
  d: AdminCustomerDetail;
  token: string;
  onRefresh: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<ActionMsg>(null);
  const [email, setEmail] = useState(d.email);
  const [pw, setPw] = useState('');
  const [licTenant, setLicTenant] = useState(d.tenants[0]?.tenantId ?? '');
  const [licTier, setLicTier] = useState<'core' | 'pro' | 'enterprise'>('pro');
  const [licExpires, setLicExpires] = useState('');

  async function run(key: string, okText: string, fn: () => Promise<AdminActionResult>): Promise<boolean> {
    setBusy(key);
    setMsg(null);
    const res = await fn();
    setBusy(null);
    setMsg(actionMessage(res, okText));
    return res.kind === 'ok';
  }
  const act = (key: string, okText: string, fn: () => Promise<AdminActionResult>): void => {
    void run(key, okText, fn).then((ok) => {
      if (ok) onRefresh();
    });
  };

  const locked = d.lockoutUntil !== null || d.failedLoginCount > 0;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{d.email}</Box>
        {d.emailVerifiedAt !== null && <Chip size="small" label="verified" color="success" variant="outlined" />}
        {locked && <Chip size="small" label="locked" color="warning" variant="outlined" />}
        {d.totpEnabled && <Chip size="small" label="2FA" variant="outlined" />}
        <Typography variant="body2" color="text.secondary">joined {formatDate(d.createdAt)}</Typography>
      </Stack>

      {msg !== null && <Alert severity={msg.kind === 'ok' ? 'success' : 'error'} role="status">{msg.text}</Alert>}

      <Panel title="Email">
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField type="email" value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth inputProps={{ maxLength: 200, 'data-testid': 'admin-customer-email' }} />
          <Button size="small" variant="contained" disabled={busy !== null || email.trim() === d.email || email.trim().length === 0} onClick={() => act('email', 'Email updated.', () => adminUpdateCustomerEmail(token, d.id, email.trim()))} data-testid="admin-customer-email-save" sx={{ flexShrink: 0 }}>
            {busy === 'email' ? 'Saving…' : 'Save email'}
          </Button>
        </Stack>
      </Panel>

      <Panel title="Password">
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField type="password" value={pw} onChange={(e) => setPw(e.target.value)} size="small" fullWidth placeholder="New password (min 10 chars)" inputProps={{ maxLength: 200, autoComplete: 'new-password', 'data-testid': 'admin-customer-password' }} />
          <Button
            size="small"
            variant="contained"
            disabled={busy !== null || pw.length < 10}
            onClick={() => void run('password', 'Password set.', () => adminSetCustomerPassword(token, d.id, pw)).then((ok) => { if (ok) { setPw(''); onRefresh(); } })}
            data-testid="admin-customer-password-save"
            sx={{ flexShrink: 0 }}
          >
            {busy === 'password' ? 'Setting…' : 'Set password'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Hashed on the server; also marks the email verified and clears any lockout.</Typography>
      </Panel>

      <Panel title="Account">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" disabled={busy !== null || !locked} onClick={() => act('unlock', 'Lockout cleared.', () => adminUnlockCustomer(token, d.id))} data-testid="admin-customer-unlock">
            {busy === 'unlock' ? 'Unlocking…' : 'Unlock'}
          </Button>
          <Button size="small" variant="outlined" disabled={busy !== null || !d.totpEnabled} onClick={() => act('2fa', '2FA reset.', () => adminResetCustomer2fa(token, d.id))} data-testid="admin-customer-reset2fa">
            {busy === '2fa' ? 'Resetting…' : 'Reset 2FA'}
          </Button>
          <Button size="small" variant="outlined" disabled={busy !== null || d.emailVerifiedAt !== null} onClick={() => act('verify', 'Email marked verified.', () => adminVerifyCustomerEmail(token, d.id))} data-testid="admin-customer-verify">
            {busy === 'verify' ? 'Verifying…' : 'Mark verified'}
          </Button>
        </Stack>
      </Panel>

      <Panel title={`Tenants (${String(d.tenants.length)})`}>
        {d.tenants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No fleets owned yet.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {d.tenants.map((t) => (
              <Box key={t.tenantId}>
                <AdminLink to="tenants" id={t.tenantId}>
                  <Box component="code" sx={{ fontFamily: 'monospace', color: 'primary.light' }}>{t.tenantId}</Box>
                </AdminLink>
                {t.deploymentName !== null && <Box component="span" sx={{ color: 'text.secondary' }}> · {t.deploymentName}</Box>}
              </Box>
            ))}
          </Stack>
        )}
      </Panel>

      <Panel title="Issue license">
        {d.tenants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Create a fleet for this customer first — licenses are issued per tenant.</Typography>
        ) : (
          <Stack spacing={2} data-testid="admin-issue">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Tenant" value={licTenant} onChange={(e) => setLicTenant(e.target.value)} size="small" fullWidth SelectProps={{ native: true, inputProps: { 'data-testid': 'admin-issue-tenant' } }}>
                {d.tenants.map((t) => (<option key={t.tenantId} value={t.tenantId}>{t.tenantId}</option>))}
              </TextField>
              <TextField select label="Tier" value={licTier} onChange={(e) => setLicTier(e.target.value as 'core' | 'pro' | 'enterprise')} size="small" fullWidth SelectProps={{ native: true, inputProps: { 'data-testid': 'admin-issue-tier' } }}>
                <option value="core">Core (free)</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </TextField>
              <TextField type="date" label="Expires (optional)" value={licExpires} onChange={(e) => setLicExpires(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'admin-issue-expires' }} />
            </Stack>
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy !== null || licTenant === ''}
                onClick={() => {
                  const epoch = licExpires.length > 0 ? Math.floor(new Date(licExpires).getTime() / 1000) : undefined;
                  void run('issue', `Issued ${licTier} license for ${licTenant}.`, () =>
                    adminIssueLicense(token, {
                      tenantId: licTenant,
                      tier: licTier,
                      ...(epoch !== undefined && Number.isFinite(epoch) && epoch > 0 ? { expiresAt: epoch } : {}),
                    }),
                  );
                }}
                data-testid="admin-issue-submit"
              >
                {busy === 'issue' ? 'Issuing…' : `Issue ${licTier} license`}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Adds a fresh signed license; the renderer picks the newest active one on next refresh. Blank expiry = perpetual.
              </Typography>
            </Box>
          </Stack>
        )}
      </Panel>

      <Panel title="Danger zone">
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={busy !== null}
          onClick={() => {
            void (async () => {
              const ok = await confirmDialog({
                title: 'Delete customer',
                message: `Permanently delete ${d.email}? This removes the customer and all ${String(d.tenants.length)} of their tenants, devices, licenses, and sessions. This cannot be undone.`,
                destructive: true,
                confirmLabel: 'Delete customer',
                requireText: d.email,
              });
              if (!ok) return;
              if (await run('delete', 'Customer deleted.', () => adminDeleteCustomer(token, d.id))) {
                onDeleted();
              }
            })();
          }}
          data-testid="admin-customer-delete"
        >
          Delete customer
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Permanently removes this customer and all {String(d.tenants.length)} of their tenants, devices, and sessions.
        </Typography>
      </Panel>
    </Stack>
  );
}

type DeviceState =
  | { id: string; s: 'loading' }
  | {
      id: string;
      s: 'ok';
      device: AdminDeviceDetail;
      latest: DeviceHealth | null;
      history: DeviceHealth[];
    }
  | { id: string; s: 'err'; message: string };

function FleetSection({
  state,
  token,
  deviceId,
  onChanged,
}: {
  state: FleetState | null;
  token: string;
  /** When set (from `#fleet/:id`), show that device's detail drill-down. */
  deviceId: string | null;
  onChanged: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [fTenant, setFTenant] = useState('');
  const [fStatus, setFStatus] = useState<'' | 'online' | 'idle' | 'stale'>('');
  const [fOs, setFOs] = useState('');
  const [fCountry, setFCountry] = useState('');
  const [device, setDevice] = useState<DeviceState | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<ActionMsg>(null);

  async function openDevice(id: string): Promise<void> {
    setDevice({ id, s: 'loading' });
    const res = await fetchAdminDevice(token, id);
    if (res.kind === 'ok')
      setDevice({ id, s: 'ok', device: res.device, latest: res.latest ?? null, history: res.history ?? [] });
    else
      setDevice({
        id,
        s: 'err',
        message:
          res.kind === 'unauthorized'
            ? 'Admin token rejected.'
            : res.kind === 'not-found'
              ? 'Device not found.'
              : res.kind === 'network'
                ? `Network: ${res.message}`
                : `Server returned ${String(res.status)}.`,
      });
  }

  // Load the device the route points at (and reload when it changes).
  useEffect(() => {
    if (deviceId !== null) void openDevice(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // Returns null on success (and refreshes the detail), or an error message.
  async function renameDevice(id: string, nickname: string): Promise<string | null> {
    const res = await adminRenameDevice(token, id, nickname);
    if (res.kind === 'ok') {
      await openDevice(id);
      return null;
    }
    return res.kind === 'unauthorized'
      ? 'Admin token rejected.'
      : res.kind === 'not-found'
        ? 'Device not found.'
        : res.kind === 'invalid'
          ? res.message
          : res.kind === 'network'
            ? `Network: ${res.message}`
            : `Server returned ${String(res.status)}.`;
  }

  // Returns null on success (navigating back to the roster + reloading), else an error.
  async function deleteDevice(id: string): Promise<string | null> {
    const res = await adminDeleteDevice(token, id);
    if (res.kind === 'ok') {
      go('fleet');
      onChanged();
      return null;
    }
    return res.kind === 'unauthorized'
      ? 'Admin token rejected.'
      : res.kind === 'not-found'
        ? 'Device not found — it may already be gone.'
        : res.kind === 'invalid'
          ? res.message
          : res.kind === 'network'
            ? `Network: ${res.message}`
            : `Server returned ${String(res.status)}.`;
  }

  if (state === null || state.s === 'loading') {
    return (
      <Panel title="Devices">
        <Typography color="text.secondary">Loading…</Typography>
      </Panel>
    );
  }
  if (state.s === 'err') {
    return (
      <Panel title="Devices">
        <Alert severity="error">{state.message}</Alert>
      </Panel>
    );
  }

  const f = state.data;
  const onlineSince = f.now - f.activeWindowSec;
  const staleBefore = f.now - f.staleWindowSec;
  const statusOf = (lastSeenAt: number): 'online' | 'idle' | 'stale' =>
    lastSeenAt >= onlineSince ? 'online' : lastSeenAt < staleBefore ? 'stale' : 'idle';

  // Filter facet options, drawn from the whole loaded fleet (not the filtered
  // view) so a selection never hides the rest of its own dropdown.
  const tenantOpts = [...new Set(f.devices.map((d) => d.tenantId))].sort();
  const osOpts = [...new Set(f.devices.map((d) => d.osVersion).filter((v): v is string => Boolean(v)))].sort();
  const countryOpts = [...new Set(f.devices.map((d) => d.country).filter((v): v is string => Boolean(v)))].sort();

  const q = query.trim().toLowerCase();
  const rows = f.devices
    .filter((d) => fTenant === '' || d.tenantId === fTenant)
    .filter((d) => fStatus === '' || statusOf(d.lastSeenAt) === fStatus)
    .filter((d) => fOs === '' || d.osVersion === fOs)
    .filter((d) => fCountry === '' || d.country === fCountry)
    .filter(
      (d) =>
        q === '' ||
        d.tenantId.toLowerCase().includes(q) ||
        (d.deviceName ?? '').toLowerCase().includes(q) ||
        (d.localIpv4 ?? '').includes(q),
    );

  // Selection is over the filtered view: "select all" toggles exactly the rows
  // the operator can currently see.
  const filteredIds = rows.map((d) => d.id);
  const selectedInView = filteredIds.filter((id) => selected.has(id));
  const allSelected = filteredIds.length > 0 && selectedInView.length === filteredIds.length;
  const toggleOne = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });

  // Run one per-device action across the whole selection, then reload + clear.
  async function runBulk(verb: string, fn: (id: string) => Promise<AdminActionResult>): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setBulkMsg(null);
    const results = await Promise.all(ids.map((id) => fn(id)));
    const ok = results.filter((r) => r.kind === 'ok').length;
    const failed = ids.length - ok;
    setBulkBusy(false);
    setSelected(new Set());
    setBulkMsg({
      kind: failed === 0 ? 'ok' : 'err',
      text:
        failed === 0
          ? `${verb} ${String(ok)} device${ok === 1 ? '' : 's'}.`
          : `${verb} ${String(ok)}; ${String(failed)} failed.`,
    });
    onChanged();
  }

  async function bulkDelete(): Promise<void> {
    const ok = await confirmDialog({
      title: 'Delete devices',
      message: `Delete ${String(selected.size)} selected device${selected.size === 1 ? '' : 's'}? They re-appear if a unit boots into the launcher again.`,
      destructive: true,
      confirmLabel: `Delete ${String(selected.size)}`,
      requireText: 'delete',
    });
    if (ok) await runBulk('Deleted', (id) => adminDeleteDevice(token, id));
  }
  async function bulkNickname(): Promise<void> {
    const name = await promptDialog({ title: 'Set nickname', message: `Nickname for ${String(selected.size)} selected device${selected.size === 1 ? '' : 's'} (blank clears it):` });
    if (name !== null) await runBulk('Renamed', (id) => adminRenameDevice(token, id, name.trim()));
  }
  async function bulkTag(op: 'add' | 'remove'): Promise<void> {
    const tag = await promptDialog({ title: op === 'add' ? 'Add tag' : 'Remove tag', message: `Tag to ${op} on ${String(selected.size)} selected device${selected.size === 1 ? '' : 's'}:`, placeholder: 'e.g. lobby' });
    if (tag !== null && tag.trim() !== '') await runBulk(op === 'add' ? 'Tagged' : 'Untagged', (id) => adminSetDeviceTag(token, id, tag.trim(), op));
  }

  const deviceColumns: GridColDef<AdminFleet['devices'][number]>[] = [
    {
      field: '__select', headerName: '', width: 48, sortable: false, filterable: false, disableColumnMenu: true, resizable: false,
      renderHeader: () => {
        // Bind via a variable so data-testid isn't excess-property-checked
        // against MUI's stricter Checkbox inputProps type.
        const inputProps = { 'aria-label': 'Select all filtered devices', 'data-testid': 'admin-fleet-selectall' };
        return (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={selectedInView.length > 0 && !allSelected}
            onChange={toggleAll}
            inputProps={inputProps}
          />
        );
      },
      renderCell: (p) => {
        const inputProps = { 'aria-label': `Select ${p.row.deviceName ?? p.row.id}`, 'data-testid': `admin-fleet-select-${p.row.id}` };
        return (
          <Checkbox
            size="small"
            checked={selected.has(p.row.id)}
            onChange={() => toggleOne(p.row.id)}
            inputProps={inputProps}
          />
        );
      },
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      valueGetter: (_v, row) => statusOf(row.lastSeenAt),
      renderCell: (p) => <Chip size="small" label={String(p.value)} color={p.value === 'online' ? 'success' : p.value === 'idle' ? 'warning' : 'default'} variant={p.value === 'stale' ? 'outlined' : 'filled'} />,
    },
    {
      field: 'device', headerName: 'Device', flex: 1, minWidth: 150,
      valueGetter: (_v, row) => row.deviceName ?? row.id,
      renderCell: (p) => (
        <AdminLink to="fleet" id={p.row.id} title="View device detail">
          {p.row.deviceName ?? <Box component="code" sx={{ fontFamily: 'monospace' }}>{p.row.id.slice(0, 8)}…</Box>}
        </AdminLink>
      ),
    },
    {
      field: 'tenantId', headerName: 'Tenant', flex: 1, minWidth: 130,
      renderCell: (p) => (
        <AdminLink to="tenants" id={String(p.value)} title="View tenant detail">
          <Box component="code" sx={{ fontFamily: 'monospace', color: 'primary.light' }}>{String(p.value)}</Box>
        </AdminLink>
      ),
    },
    { field: 'osVersion', headerName: 'Android', width: 100, renderCell: (p) => p.value ?? '—' },
    { field: 'securityPatch', headerName: 'Patch', width: 120, renderCell: (p) => p.value ?? '—' },
    { field: 'localIpv4', headerName: 'LAN IP', width: 130, renderCell: (p) => p.value ?? '—' },
    { field: 'bootCount', headerName: 'Boots', width: 90, type: 'number' },
    { field: 'lastSeenAt', headerName: 'Last seen', width: 150, renderCell: (p) => formatDateTime(Number(p.value)) },
  ];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }} data-testid="admin-fleet">
        <Stat label="Devices" value={String(f.counts.total)} sub={f.capped ? 'capped at 1000' : 'across all tenants'} icon={<PhoneAndroidIcon />} />
        <Stat label="Online" value={String(f.counts.online)} sub="seen in last 15 min" icon={<GridViewIcon />} />
        <Stat label="Stale" value={String(f.counts.stale)} sub="not seen in 24h+" icon={<StorageIcon />} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {f.osSpread.length > 0 && <Panel title="Android version"><MiniBars rows={f.osSpread} /></Panel>}
        {f.patchSpread.length > 0 && <Panel title="Security patch level"><MiniBars rows={f.patchSpread} /></Panel>}
      </Box>

      {deviceId !== null ? (
        <DeviceDetailView
          state={device ?? { id: deviceId, s: 'loading' }}
          status={statusOf}
          onRename={(nickname) => renameDevice(deviceId, nickname)}
          onDelete={() => deleteDevice(deviceId)}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Typography variant="h5" fontWeight={700}>
                Devices ({String(rows.length)}{rows.length !== f.devices.length ? ` of ${String(f.devices.length)}` : ''})
              </Typography>
              <FilterSelect label="Filter by tenant" value={fTenant} onChange={setFTenant} testId="admin-fleet-ftenant" options={[{ value: '', label: 'All tenants' }, ...tenantOpts.map((t) => ({ value: t, label: t }))]} />
              <FilterSelect label="Filter by status" value={fStatus} onChange={(v) => setFStatus(v as '' | 'online' | 'idle' | 'stale')} testId="admin-fleet-fstatus" options={[{ value: '', label: 'Any status' }, { value: 'online', label: 'Online' }, { value: 'idle', label: 'Idle' }, { value: 'stale', label: 'Stale' }]} />
              <FilterSelect label="Filter by Android version" value={fOs} onChange={setFOs} testId="admin-fleet-fos" options={[{ value: '', label: 'Any Android' }, ...osOpts.map((v) => ({ value: v, label: v }))]} />
              <FilterSelect label="Filter by country" value={fCountry} onChange={setFCountry} testId="admin-fleet-fcountry" options={[{ value: '', label: 'Any country' }, ...countryOpts.map((v) => ({ value: v, label: v }))]} />
              <AdminSearchField value={query} onChange={setQuery} placeholder="Search tenant, name, IP…" />
            </Stack>
            <Tooltip title="Export CSV">
              <IconButton
                data-testid="admin-export"
                aria-label="Export CSV"
                onClick={() =>
                  downloadCsv(
                    'devices.csv',
                    ['Status', 'Device', 'Tenant', 'Android', 'Patch', 'LAN IP', 'Country', 'Boots', 'Last seen'],
                    rows.map((d) => [statusOf(d.lastSeenAt), d.deviceName ?? d.id, d.tenantId, d.osVersion ?? '', d.securityPatch ?? '', d.localIpv4 ?? '', d.country ?? '', d.bootCount, formatDateTime(d.lastSeenAt)]),
                  )
                }
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {selected.size > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="admin-fleet-bulkbar" sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.selected' }}>
              <Typography variant="body2" fontWeight={600}>{String(selected.size)} selected</Typography>
              <Button size="small" disabled={bulkBusy} onClick={() => void bulkNickname()} data-testid="admin-bulk-nickname">Set nickname</Button>
              <Button size="small" disabled={bulkBusy} onClick={() => void bulkTag('add')} data-testid="admin-bulk-tag-add">Add tag</Button>
              <Button size="small" disabled={bulkBusy} onClick={() => void bulkTag('remove')} data-testid="admin-bulk-tag-remove">Remove tag</Button>
              <Button size="small" color="error" disabled={bulkBusy} onClick={() => void bulkDelete()} data-testid="admin-bulk-delete">Delete</Button>
              <Button size="small" color="inherit" disabled={bulkBusy} onClick={() => setSelected(new Set())}>{bulkBusy ? 'Working…' : 'Clear'}</Button>
            </Stack>
          )}
          {bulkMsg !== null && <Alert severity={bulkMsg.kind === 'ok' ? 'success' : 'error'} role="status" sx={{ mb: 1 }}>{bulkMsg.text}</Alert>}

          <Box sx={{ flexGrow: 1, minHeight: 360, minWidth: 0 }}>
            <DataGrid
              rows={rows}
              columns={deviceColumns}
              getRowId={(r) => r.id}
              density="compact"
              disableRowSelectionOnClick
              initialState={{ sorting: { sortModel: [{ field: 'lastSeenAt', sort: 'desc' }] }, pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50, 100]}
              getRowClassName={(p) => (selected.has(String(p.id)) ? 'admin-row--selected' : '')}
              sx={{ '& .admin-row--selected': { bgcolor: 'action.selected' } }}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
}

function DeviceDetailView({
  state,
  status,
  onRename,
  onDelete,
}: {
  state: DeviceState;
  status: (lastSeenAt: number) => 'online' | 'idle' | 'stale';
  onRename: (nickname: string) => Promise<string | null>;
  onDelete: () => Promise<string | null>;
}): JSX.Element {
  return (
    <Box data-testid="admin-device-detail">
      <BackLink to="fleet">← All devices</BackLink>
      {state.s === 'loading' ? (
        <Typography color="text.secondary">Loading {state.id}…</Typography>
      ) : state.s === 'err' ? (
        <Alert severity="error">{state.message}</Alert>
      ) : (
        <DeviceDetailBody
          d={state.device}
          latest={state.latest}
          history={state.history}
          status={status}
          onRename={onRename}
          onDelete={onDelete}
        />
      )}
    </Box>
  );
}

function DeviceDetailBody({
  d,
  latest,
  history,
  status,
  onRename,
  onDelete,
}: {
  d: AdminDeviceDetail;
  latest: DeviceHealth | null;
  history: DeviceHealth[];
  status: (lastSeenAt: number) => 'online' | 'idle' | 'stale';
  onRename: (nickname: string) => Promise<string | null>;
  onDelete: () => Promise<string | null>;
}): JSX.Element {
  const st = status(d.lastSeenAt);
  const nickname = typeof d.metadata?.['nickname'] === 'string' ? d.metadata['nickname'] : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(nickname ?? '');
  const [busy, setBusy] = useState(false);
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  async function removeDevice(): Promise<void> {
    const ok = await confirmDialog({
      title: 'Delete device',
      message:
        'Delete this device record? It re-appears if the unit boots into the launcher again.',
      destructive: true,
      confirmLabel: 'Delete device',
    });
    if (!ok) return;
    setDeleting(true);
    setDeleteErr(null);
    const err = await onDelete();
    // On success the parent pops back to the roster (this body unmounts), so we
    // only handle the failure path here.
    if (err !== null) {
      setDeleting(false);
      setDeleteErr(err);
    }
  }

  async function saveNickname(): Promise<void> {
    setBusy(true);
    setRenameErr(null);
    // On success the parent re-fetches and remounts this body, so we don't
    // need to clear edit state here; on failure we stay open with the error.
    const err = await onRename(draft.trim());
    setBusy(false);
    if (err !== null) setRenameErr(err);
  }

  const nicknameValue: ReactNode = editing ? (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField value={draft} onChange={(e) => setDraft(e.target.value)} size="small" placeholder="Lobby kiosk" inputProps={{ maxLength: 60, 'data-testid': 'admin-rename-input' }} />
      <Button size="small" variant="contained" disabled={busy} onClick={() => void saveNickname()} data-testid="admin-rename-save">{busy ? 'Saving…' : 'Save'}</Button>
      <Button size="small" disabled={busy} onClick={() => { setEditing(false); setRenameErr(null); }}>Cancel</Button>
      {renameErr !== null && <Typography variant="caption" color="error" role="alert">{renameErr}</Typography>}
    </Stack>
  ) : (
    <Stack direction="row" spacing={1} alignItems="center">
      <span>{nickname ?? d.deviceName ?? em()}</span>
      <Button size="small" onClick={() => { setDraft(nickname ?? ''); setEditing(true); }} data-testid="admin-rename-edit" sx={{ minWidth: 0 }}>Edit</Button>
    </Stack>
  );

  const rows: { label: string; value: ReactNode }[] = [
    { label: 'Device ID', value: <Box component="code" sx={{ fontFamily: 'monospace' }}>{d.id}</Box> },
    { label: 'Nickname', value: nicknameValue },
    {
      label: 'Tenant',
      value: (
        <AdminLink to="tenants" id={d.tenantId}>
          <Box component="span" sx={{ color: 'primary.light' }}>{d.tenantId}</Box>
        </AdminLink>
      ),
    },
    { label: 'Android version', value: d.osVersion ?? em() },
    { label: 'Security patch', value: d.securityPatch ?? em() },
    { label: 'App version', value: d.appVersion ?? em() },
    { label: 'Android ID', value: d.androidId ?? em() },
    { label: 'LAN IPv4', value: d.localIpv4 ?? em() },
    { label: 'Egress IP', value: d.ipAddress ?? em() },
    {
      label: 'Location',
      value: [d.city, d.country].filter(Boolean).join(', ') || em(),
    },
    { label: 'Boots', value: String(d.bootCount) },
    { label: 'First seen', value: formatDateTime(d.firstSeenAt) },
    { label: 'Last seen', value: formatDateTime(d.lastSeenAt) },
  ];

  const gb = (b: number): string => `${(b / 1_000_000_000).toFixed(1)} GB`;
  const uptime = (s: number): string => {
    const days = Math.floor(s / 86_400);
    const hrs = Math.floor((s % 86_400) / 3600);
    return days > 0 ? `${String(days)}d ${String(hrs)}h` : `${String(hrs)}h`;
  };
  const healthRows: { label: string; value: ReactNode }[] =
    latest === null
      ? []
      : [
          { label: 'Reported', value: formatDateTime(latest.reportedAt) },
          { label: 'Tier', value: latest.tier ?? em() },
          {
            label: 'License',
            value:
              latest.licenseStatus === null ? (
                em()
              ) : latest.licenseStatus === 'invalid' ? (
                // The silent-downgrade case: a license was present but rejected.
                <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                  invalid (fell back to Core)
                </Box>
              ) : (
                latest.licenseStatus
              ),
          },
          // The id of the license the device is on — copyable, so an 'invalid'
          // device can be traced straight to its license row / "Revoke by id".
          ...(latest.licenseId !== null
            ? [
                {
                  label: 'License ID',
                  value: (
                    <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, userSelect: 'all' }}>
                      {latest.licenseId}
                    </Box>
                  ),
                },
              ]
            : []),
          {
            label: 'Online',
            value: latest.online === null ? em() : latest.online ? 'Yes' : 'No',
          },
          {
            label: 'Battery',
            value:
              latest.batteryPct === null
                ? em()
                : `${String(latest.batteryPct)}%${latest.batteryCharging ? ' (charging)' : ''}`,
          },
          { label: 'Signal', value: latest.signalDbm === null ? em() : `${String(latest.signalDbm)} dBm` },
          {
            label: 'Storage',
            value:
              latest.storageTotalBytes === null
                ? em()
                : `${latest.storageUsedBytes !== null ? gb(latest.storageUsedBytes) : '?'} / ${gb(latest.storageTotalBytes)}`,
          },
          {
            label: 'Memory',
            value:
              latest.memoryTotalMb === null
                ? em()
                : `${latest.memoryUsedMb !== null ? String(latest.memoryUsedMb) : '?'} / ${String(latest.memoryTotalMb)} MB`,
          },
          { label: 'Uptime', value: latest.uptimeSec === null ? em() : uptime(latest.uptimeSec) },
          { label: 'Config version', value: latest.configVersion ?? em() },
        ];

  // Trend charts from the health history (server returns newest-first; reverse
  // to oldest→newest for a left-to-right timeline). Percent series so battery /
  // storage / memory share one 0–100 axis. A series with <2 real points is
  // dropped (nothing to plot).
  const trend = [...history].reverse();
  const pct = (used: number | null, total: number | null): number | null =>
    total !== null && total > 0 && used !== null ? Math.round((used / total) * 100) : null;
  const trendX = trend.map((h) =>
    new Date(h.reportedAt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  );
  const trendSeries = [
    { label: 'Battery %', color: '#4caf50', data: trend.map((h) => h.batteryPct) },
    {
      label: 'Storage used %',
      color: '#2196f3',
      data: trend.map((h) => pct(h.storageUsedBytes, h.storageTotalBytes)),
    },
    {
      label: 'Memory used %',
      color: '#ff9800',
      data: trend.map((h) => pct(h.memoryUsedMb, h.memoryTotalMb)),
    },
  ].filter((s) => s.data.filter((v) => v !== null).length >= 2);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <FiberManualRecordIcon sx={{ fontSize: 12, color: st === 'online' ? 'success.main' : st === 'idle' ? 'warning.main' : 'text.disabled' }} />
        <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{nickname ?? d.deviceName ?? d.id.slice(0, 12)}</Box>
        <Typography variant="body2" color="text.secondary">{st}</Typography>
      </Stack>
      <Panel title="Device record">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 3, rowGap: 1.25, alignItems: 'center' }}>
          {rows.map((r) => (
            <Fragment key={r.label}>
              <Typography variant="body2" color="text.secondary">{r.label}</Typography>
              <Box sx={{ fontSize: 14 }}>{r.value}</Box>
            </Fragment>
          ))}
        </Box>
      </Panel>
      {latest !== null && (
        <Panel title="Health (latest heartbeat)">
          <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 3, rowGap: 1.25, alignItems: 'center' }} data-testid="admin-device-health">
            {healthRows.map((r) => (
              <Fragment key={r.label}>
                <Typography variant="body2" color="text.secondary">{r.label}</Typography>
                <Box sx={{ fontSize: 14 }}>{r.value}</Box>
              </Fragment>
            ))}
          </Box>
        </Panel>
      )}
      {trendSeries.length > 0 && (
        <Panel title="Health trends (recent heartbeats)">
          <Box data-testid="admin-device-trends">
            <LineChart
              height={240}
              xAxis={[{ scaleType: 'point', data: trendX }]}
              yAxis={[{ min: 0, max: 100 }]}
              series={trendSeries.map((s) => ({
                data: s.data,
                label: s.label,
                color: s.color,
                showMark: false,
                connectNulls: false,
              }))}
              margin={{ left: 40, right: 16, top: 16, bottom: 24 }}
            />
          </Box>
        </Panel>
      )}
      <Panel title="Danger zone">
        <Button size="small" variant="outlined" color="error" disabled={deleting} onClick={() => void removeDevice()} data-testid="admin-device-delete">
          {deleting ? 'Deleting…' : 'Delete device'}
        </Button>
        {deleteErr !== null && <Alert severity="error" sx={{ mt: 1 }}>{deleteErr}</Alert>}
      </Panel>
    </Stack>
  );
}

function em(): JSX.Element {
  return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
}

/** Horizontal bar list for a label→count spread (top 8). */
function MiniBars({ rows }: { rows: { label: string; count: number }[] }): JSX.Element {
  const top = rows.slice(0, 8);
  return (
    <BarChart
      layout="horizontal"
      height={Math.max(140, top.length * 38)}
      yAxis={[{ scaleType: 'band', data: top.map((r) => r.label) }]}
      series={[{ data: top.map((r) => r.count), color: '#1976d2' }]}
      margin={{ left: 80, right: 16, top: 8, bottom: 24 }}
      slotProps={{ legend: { hidden: true } }}
    />
  );
}

/** Order + colors for the real-vs-bot buckets (independent of API row order). */
const SIGNUP_SOURCES: { key: string; label: string; color: string }[] = [
  { key: 'likely-human', label: 'Likely human', color: '#4ade80' },
  { key: 'unknown', label: 'Unknown', color: 'rgba(232,234,237,0.32)' },
  { key: 'bot-ish', label: 'Bot-ish', color: '#f0b429' },
  { key: 'verified-bot', label: 'Verified bot', color: '#ff6b6b' },
];

const DAY_OPTIONS = [7, 30, 90];

function AnalyticsSection({
  state,
  days,
  onDays,
}: {
  state: AnalyticsState | null;
  days: number;
  onDays: (d: number) => void;
}): JSX.Element {
  const ranges = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={days}
      onChange={(_e, v) => { if (v !== null) onDays(v as number); }}
      aria-label="Time range"
    >
      {DAY_OPTIONS.map((d) => (<ToggleButton key={d} value={d}>{String(d)}d</ToggleButton>))}
    </ToggleButtonGroup>
  );

  if (state === null || state.s === 'loading') {
    return <Panel title="Analytics"><Typography color="text.secondary">Loading…</Typography></Panel>;
  }
  if (state.s === 'err') {
    return <Panel title="Analytics"><Alert severity="error">{state.message}</Alert></Panel>;
  }

  const data = state.data;
  if (!data.configured) {
    return (
      <Panel title="Analytics">
        <Typography color="text.secondary">
          Analytics isn’t wired up yet. Set <Box component="code">CF_ACCOUNT_ID</Box> and{' '}
          <Box component="code">CF_API_TOKEN</Box> (a token with <em>Account Analytics: Read</em>) on the
          licensing Worker, then refresh. Until then the rest of the dashboard works without it.
        </Typography>
      </Panel>
    );
  }
  if ('error' in data) {
    return <Panel title="Analytics"><Alert severity="error">Analytics Engine query failed: {data.error}</Alert></Panel>;
  }

  const segments = SIGNUP_SOURCES.map((src) => ({
    label: src.label,
    value: data.signups.find((s) => s.source === src.key)?.signups ?? 0,
    color: src.color,
  })).filter((s) => s.value > 0);
  const totalSignups = segments.reduce((sum, s) => sum + s.value, 0);
  const human = segments.find((s) => s.label === 'Likely human')?.value ?? 0;
  const blockedTotal = data.blocked.reduce((sum, b) => sum + b.blocked, 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary">Last {String(data.days)} days</Typography>
        {ranges}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }} data-testid="admin-analytics">
        <Stat label="New signups" value={String(totalSignups)} icon={<PeopleIcon />} />
        <Stat
          label="Likely human"
          value={totalSignups > 0 ? `${String(Math.round((human / totalSignups) * 100))}%` : '—'}
          sub={`${String(human)} of ${String(totalSignups)}`}
          icon={<BarChartIcon />}
        />
        <Stat label="Turnstile blocked" value={String(blockedTotal)} sub="bots stopped at the gate" icon={<VpnKeyIcon />} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {segments.length > 0 && (
          <Panel title="New signups — real vs bot">
            <Donut segments={segments} total={totalSignups} centerLabel="signups" />
          </Panel>
        )}
        <Panel title="Funnel (event × outcome)">
          {data.funnel.length === 0 ? (
            <Typography color="text.secondary">No events recorded in this window.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Event</TableCell><TableCell>Outcome</TableCell><TableCell>Count</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {data.funnel.map((row) => (
                  <TableRow key={`${row.event}:${row.outcome}`}>
                    <TableCell><Box component="code" sx={{ fontFamily: 'monospace' }}>{row.event}</Box></TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.outcome === '' ? '—' : row.outcome}</TableCell>
                    <TableCell>{String(row.n)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      </Box>

      {blockedTotal === 0 && data.funnel.length > 0 && (
        <Typography variant="body2" color="text.secondary">No Turnstile rejections in this window.</Typography>
      )}
    </Stack>
  );
}

function Stat({
  label,
  value,
  sub,
  icon,
  delta,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: ReactNode;
  delta?: number | undefined;
}): JSX.Element {
  return (
    <Card>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Box sx={{ color: 'text.secondary', display: 'flex' }} aria-hidden>{icon}</Box>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          {delta !== undefined && delta !== 0 && (
            <Stack direction="row" alignItems="center" sx={{ color: delta > 0 ? 'success.main' : 'error.main' }}>
              {delta > 0 ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
              <Typography variant="caption">{String(Math.abs(delta))}</Typography>
            </Stack>
          )}
        </Stack>
        {sub !== undefined && <Typography variant="caption" color="text.secondary" display="block">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
function formatUsd(cents: number): string {
  return USD.format(cents / 100);
}

function formatDate(unixSec: number): string {
  return new Date(unixSec * 1000).toISOString().slice(0, 10);
}

function formatDateTime(unixSec: number): string {
  return new Date(unixSec * 1000).toISOString().slice(0, 16).replace('T', ' ');
}

/** Like formatDateTime but for a millisecond epoch (telemetry receivedAt). */
function formatMsDateTime(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16).replace('T', ' ');
}

